import Num, { BigNumber } from '@flipster/utils/num';

/**
 * Aggregate orderbook data by grouping prices into buckets of the selected tick size.
 * - bid: floor(price / tickSize) * tickSize
 * - ask: ceil(price / tickSize) * tickSize
 * Quantities within the same bucket are summed.
 * Returns [bucketPrice, totalQty, notional] where notional = sum of (originalPrice × qty)
 * so that avgPrice can be accurately calculated as notional / totalQty.
 */
export function aggregateOrderbook(
  data: [price: string, qty: string][],
  side: 'bid' | 'ask',
  selectedTickSize: string,
  baseTickSize: string,
): [string, string, string][] {
  if (selectedTickSize === baseTickSize) {
    return data.map(([price, qty]) => [price, qty, Num(price).times(qty).toString()]);
  }

  const tick = Num(selectedTickSize);
  if (tick.isZero() || tick.isNaN()) {
    return data.map(([price, qty]) => [price, qty, Num(price).times(qty).toString()]);
  }

  const buckets = new Map<string, { qty: BigNumber; notional: BigNumber }>();

  for (const [price, qty] of data) {
    const p = Num(price);
    const q = Num(qty);
    const bucket =
      side === 'bid'
        ? p.dividedBy(tick).integerValue(BigNumber.ROUND_FLOOR).times(tick)
        : p.dividedBy(tick).integerValue(BigNumber.ROUND_CEIL).times(tick);

    const key = bucket.toString();
    const existing = buckets.get(key);
    if (existing) {
      buckets.set(key, {
        qty: existing.qty.plus(q),
        notional: existing.notional.plus(p.times(q)),
      });
    } else {
      buckets.set(key, { qty: q, notional: p.times(q) });
    }
  }

  return Array.from(buckets.entries()).map(([price, { qty, notional }]) => [
    price,
    qty.toString(),
    notional.toString(),
  ]);
}
